import { assert, NormalizedNumber } from '@sparkdotfi/common-universal'

import { WalletOverview } from '../types'
import { UseMarketDetailsResult } from './useMarketDetails'
import { NativeAssetInfo } from '@/config/chain/types'
import { OpenDialogFunction } from '@/domain/state/dialogs/slice'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { MarketInfo, Reserve } from '@/features/sparklend/common/data/market-info/marketInfo'
import { MarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { getBorrowMaxValue } from '@/features/sparklend/common/utils/action-max-value-getters/getBorrowMaxValue'
import { getDepositMaxValue } from '@/features/sparklend/common/utils/action-max-value-getters/getDepositMaxValue'
import { getValidateBorrowArgs, validateBorrow } from '@/features/sparklend/common/validation/validateBorrow'
import { borrowDialogConfig } from '@/features/sparklend/dialogs/borrow/BorrowDialog'
import { depositDialogConfig } from '@/features/sparklend/dialogs/deposit/DepositDialog'
import { applyTransformers } from '@/utils/applyTransformers'

export interface MakeWalletOverviewParams {
  reserve: Reserve
  walletInfo: MarketWalletInfo
  marketInfo: MarketInfo
  connectedChainId: number
  nativeAssetInfo: NativeAssetInfo
  openDialog: OpenDialogFunction
  usdcViaUsdsProxyMarket: UseMarketDetailsResult['usdcViaUsdsProxyMarket']
}

export function makeWalletOverview({
  reserve,
  marketInfo,
  walletInfo,
  connectedChainId,
  nativeAssetInfo,
  openDialog,
  usdcViaUsdsProxyMarket,
}: MakeWalletOverviewParams): WalletOverview {
  const overview = applyTransformers({
    reserve,
    marketInfo,
    walletInfo,
    connectedChainId,
    nativeAssetInfo,
    openDialog,
    usdcViaUsdsProxyMarket,
  })([
    makeGuestModeOverview,
    makeChainMismatchOverview,
    makeUsdcViaUsdsProxyMarketOverview,
    makeWalletNativeAssetOverview,
    makeBaseWalletOverview,
  ])
  assert(overview, 'The only item was skipped by transformers.')

  return overview
}

function makeGuestModeOverview({ reserve, walletInfo }: MakeWalletOverviewParams): WalletOverview | undefined {
  if (walletInfo.isConnected) {
    return undefined
  }

  const token = reserve.token

  return {
    guestMode: true,
    token,
    tokenBalance: walletInfo.findWalletBalanceForToken(token),
    deposit: {
      token,
      available: NormalizedNumber.ZERO,
      openDepositDialog: () => {},
    },
    borrow: {
      token,
      available: NormalizedNumber.ZERO,
      eligibility: reserve.borrowEligibilityStatus,
      openBorrowDialog: () => {},
    },
  }
}

function makeChainMismatchOverview({
  reserve,
  marketInfo,
  walletInfo,
  connectedChainId,
  openDialog,
}: MakeWalletOverviewParams): WalletOverview | undefined {
  if (connectedChainId === marketInfo.chainId) {
    return undefined
  }

  const token = reserve.token

  return {
    guestMode: false,
    token,
    tokenBalance: walletInfo.findWalletBalanceForToken(token),
    deposit: {
      token,
      available: NormalizedNumber.ZERO,
      openDepositDialog: () => openDialog(depositDialogConfig, { token }),
    },
    borrow: {
      token,
      available: NormalizedNumber.ZERO,
      eligibility: reserve.borrowEligibilityStatus,
      openBorrowDialog: () => openDialog(borrowDialogConfig, { reserve, initialBorrowToken: token }),
    },
  }
}

function makeBaseWalletOverview({
  reserve,
  marketInfo,
  walletInfo,
  openDialog,
}: MakeWalletOverviewParams): WalletOverview {
  const token = reserve.token
  const tokenBalance = walletInfo.findWalletBalanceForToken(token)

  const availableToDeposit = getDepositMaxValue({
    asset: {
      status: reserve.status,
      totalLiquidity: reserve.totalLiquidity,
      isNativeAsset: marketInfo.nativeAssetInfo.nativeAssetName === token.symbol,
      supplyCap: reserve.supplyCap,
    },
    user: {
      balance: tokenBalance,
    },
    chain: {
      minRemainingNativeAsset: marketInfo.nativeAssetInfo.minRemainingNativeAssetBalance,
    },
  })

  const borrowValidationArgs = getValidateBorrowArgs(NormalizedNumber.ZERO, reserve, marketInfo)
  const validationIssue = validateBorrow(borrowValidationArgs)

  const availableToBorrow = getBorrowMaxValue({
    validationIssue,
    user: borrowValidationArgs.user,
    asset: borrowValidationArgs.asset,
  })

  return {
    guestMode: false,
    token,
    tokenBalance,
    deposit: {
      token,
      available: availableToDeposit,
      openDepositDialog: () => openDialog(depositDialogConfig, { token }),
    },
    borrow: {
      token,
      available: availableToBorrow,
      eligibility: reserve.borrowEligibilityStatus,
      openBorrowDialog: () => openDialog(borrowDialogConfig, { reserve, initialBorrowToken: token }),
    },
  }
}

function makeWalletNativeAssetOverview({
  reserve,
  walletInfo,
  nativeAssetInfo,
  ...rest
}: MakeWalletOverviewParams): WalletOverview | undefined {
  if (reserve.token.symbol !== nativeAssetInfo.wrappedNativeAssetSymbol) {
    return undefined
  }

  const baseOverview = makeBaseWalletOverview({ reserve, nativeAssetInfo, walletInfo, ...rest })

  const tokenBalance = walletInfo
    .findWalletBalanceForToken(reserve.token)
    .plus(walletInfo.findWalletBalanceForSymbol(nativeAssetInfo.nativeAssetSymbol))

  const availableToDeposit = getDepositMaxValue({
    asset: {
      status: reserve.status,
      totalLiquidity: reserve.totalLiquidity,
      isNativeAsset: true,
      supplyCap: reserve.supplyCap,
    },
    user: {
      balance: tokenBalance,
    },
    chain: {
      minRemainingNativeAsset: nativeAssetInfo.minRemainingNativeAssetBalance,
    },
  })

  return {
    ...baseOverview,
    tokenBalance,
    deposit: {
      ...baseOverview.deposit,
      available: availableToDeposit,
    },
  }
}

function makeUsdcViaUsdsProxyMarketOverview(params: MakeWalletOverviewParams): WalletOverview | undefined {
  const { usdcViaUsdsProxyMarket, openDialog, reserve, marketInfo, walletInfo } = params
  if (!usdcViaUsdsProxyMarket?.selected) {
    return undefined
  }

  const baseOverview = makeBaseWalletOverview(params)
  const usdc = marketInfo.findOneBySymbol(TokenSymbol('USDC'))
  const usdcBalance = walletInfo.findWalletBalanceForToken(usdc)

  return {
    ...baseOverview,
    token: usdc,
    tokenBalance: usdcBalance,
    borrow: {
      ...baseOverview.borrow,
      token: usdc,
      openBorrowDialog: () => openDialog(borrowDialogConfig, { reserve, initialBorrowToken: usdc }),
    },
    deposit: {
      ...baseOverview.deposit,
      token: usdc,
      openDepositDialog: () => openDialog(depositDialogConfig, { token: reserve.token }),
      disabled: true,
    },
  }
}
